import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { useAwsCognitoActions } from "../../contexts/AWSAuthContext";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import NewPasswordModal from "./NewPasswordModal";
import ForgotPasswordModal from "./ForgotPasswordModal";
import { useSnackbar } from "../../context/SnackbarContext";
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline";
import { signOut } from "aws-amplify/auth";
import {
  checkEmailApi,
  getUserConsentApi,
  getUserDataApi,
} from "../../services/axios/AuthApis/UserMasterApis";
import apiConfig from "../../services/axios/ApiConfig";

const THEME = "#26256b";
const THEME_LIGHT = "#3230a0";
const ACCENT = "#4f9cf9";

const Login = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showNewPasswordModal, setShowNewPasswordModal] = useState(false);
  const [challengeData, setChallengeData] = useState(null);
  const [emailFocused, setEmailFocused] = useState(false);
  const [passFocused, setPassFocused] = useState(false);
  const [loginError, setLoginError] = useState('');
  // null = closed, "request" = ask for a code, "confirm" = already has a code
  const [forgotPasswordStep, setForgotPasswordStep] = useState(null);

  const { isAuthenticated, user, setUser, setIsAuthenticated } = useAuth();
  const { signIn, confirmSignIn } = useAwsCognitoActions();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();

  useEffect(() => {
    if (isAuthenticated && user) navigate("/central-dashboard");
  }, [isAuthenticated, user, navigate]);

  const handleUserNavigate = async (cognitoSuccess, consent) => {
    if (!cognitoSuccess) return;
    setLoading(true);
    try {
      const userData = await getUserDataApi();
      setUser(userData);
      setIsAuthenticated(true);
      localStorage.setItem("userConsent", consent);
      if (userData.role === "customer_admin") {
        try {
          const [defaultRes, addRes] = await Promise.all([
            apiConfig.get(`/api/ai-crm/api/v1/customer-requirements/${userData.id}/?type=Default`),
            apiConfig.get(`/api/ai-crm/api/v1/customer-requirements/${userData.id}/?type=AdditionalModule`),
          ]);
          const combined = [...new Set([
            ...Object.keys(defaultRes.data?.data || {}).filter((k) => defaultRes.data.data[k] === true),
            ...Object.keys(addRes.data?.data || {}).filter((k) => addRes.data.data[k] === true),
          ])];
          localStorage.setItem("enabledModules", JSON.stringify(combined));
        } catch (err) {
          console.error("❌ Error fetching enabled modules:", err);
        }
      }
      showSnackbar("Login successful!", "success");
      navigate("/central-dashboard");
    } catch (err) {
      console.error("Error fetching user data:", err);
    }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setLoginError('');
    try {
      const { exists } = await checkEmailApi(username);
      if (!exists) {
        setLoginError("No account found with this email address.");
        setLoading(false);
        return;
      }
      await signOut();
      const cognitoResult = await signIn({ email: username, password });
      if (cognitoResult?.nextStep === "NEW_PASSWORD_REQUIRED") {
        setChallengeData(cognitoResult.challengeData);
        setShowNewPasswordModal(true);
        setLoading(false);
        return;
      }
      if (cognitoResult?.nextStep === "RESET_PASSWORD") {
        // An administrator reset this password; the code is already emailed.
        setForgotPasswordStep("confirm");
        setLoading(false);
        return;
      }
      if (cognitoResult.success) {
        try {
          const res = await getUserConsentApi(username);
          if (res?.consent_given) {
            await handleUserNavigate(true, true);
          } else {
            localStorage.setItem("pendingUserEmail", username);
            navigate("/consent", { state: { username } });
          }
        } catch (error) {
          console.error("Error fetching consent status:", error);
        }
      } else if (cognitoResult?.error) {
        setLoginError(cognitoResult.error);
      }
    } catch (cognitoError) {
      setLoginError(cognitoError?.message || "Login failed. Please check your credentials.");
    }
    setLoading(false);
  };

  const handleNewPasswordSubmit = async (newPassword) => {
    setLoading(true);
    try {
      const result = await confirmSignIn({ newPassword });
      if (result?.success) {
        setShowNewPasswordModal(false);
        setChallengeData(null);
        localStorage.setItem("pendingUserEmail", username);
        navigate("/consent", { state: { username } });
      } else {
        showSnackbar("Failed to set new password. Please try again.", "error");
      }
    } catch (error) {
      showSnackbar(error.message || "Error setting new password", "error");
    }
    setLoading(false);
  };

  const handlePasswordResetDone = (email) => {
    setForgotPasswordStep(null);
    setUsername(email);
    setPassword("");
    setLoginError('');
    showSnackbar("Password changed. Sign in with your new password.", "success");
  };

  const demoCredentials = [
    { label: "Platform Admin",   username: "divya@habilesec.com",          password: "Test@123" },
    // { label: "Customer Admin",   username: "sophiam+ca004@habilesec.com",   password: "Test@123" },
    // { label: "Security Analyst", username: "deepas@habilesec.com",          password: "Test@123" },
    // { label: "Auditor",          username: "sophiam+au001@habilesec.com",   password: "Test@123" },
    // { label: "End User",         username: "sophiam+eu020@habilesec.com",   password: "Test@123" },
  ];

  return (
    <div style={{ display: "flex", minHeight: "100vh", fontFamily: "'Inter', sans-serif" }}>

      {/* ── LEFT PANEL ─────────────────────────────────────── */}
      <div
        className="hidden lg:flex"
        style={{
          width: "45%",
          backgroundColor: THEME,
          flexDirection: "column",
          justifyContent: "space-between",
          padding: "52px 56px",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Subtle pattern overlay */}
        <div style={{
          position: "absolute", inset: 0, opacity: 0.06,
          backgroundImage: `repeating-linear-gradient(
            0deg, transparent, transparent 40px, rgba(255,255,255,1) 40px, rgba(255,255,255,1) 41px
          ), repeating-linear-gradient(
            90deg, transparent, transparent 40px, rgba(255,255,255,1) 40px, rgba(255,255,255,1) 41px
          )`,
          pointerEvents: "none",
        }} />

        {/* Glow blobs */}
        <div style={{
          position: "absolute", top: -80, right: -80,
          width: 320, height: 320, borderRadius: "50%",
          background: `radial-gradient(circle, ${THEME_LIGHT}88 0%, transparent 70%)`,
          pointerEvents: "none",
        }} />
        <div style={{
          position: "absolute", bottom: -60, left: -60,
          width: 260, height: 260, borderRadius: "50%",
          background: `radial-gradient(circle, ${ACCENT}22 0%, transparent 70%)`,
          pointerEvents: "none",
        }} />

        {/* Brand */}
        <div style={{ position: "relative", zIndex: 1, display: "flex", alignItems: "center", gap: 14 }}>
          <div style={{
            width: 44, height: 44, borderRadius: 12,
            background: "rgba(255,255,255,0.15)",
            border: "1.5px solid rgba(255,255,255,0.25)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <span style={{ color: "#fff", fontWeight: 800, fontSize: 22, letterSpacing: "-1px" }}>T</span>
          </div>
          <div>
            <div style={{ color: "#fff", fontWeight: 700, fontSize: 18, letterSpacing: "-0.3px" }}>Tanikkai</div>
            <div style={{ color: "rgba(255,255,255,0.5)", fontSize: 12, fontWeight: 500 }}>GRC Platform</div>
          </div>
        </div>

        {/* Hero content */}
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.15 }}
          style={{ position: "relative", zIndex: 1 }}
        >
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            background: "rgba(255,255,255,0.1)",
            border: "1px solid rgba(255,255,255,0.15)",
            borderRadius: 99, padding: "5px 14px", marginBottom: 24,
          }}>
            <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#4ade80" }} />
            <span style={{ color: "rgba(255,255,255,0.8)", fontSize: 12, fontWeight: 500 }}>
              AI-Powered Compliance Intelligence
            </span>
          </div>

          <h1 style={{
            color: "#fff", fontSize: 40, fontWeight: 800,
            lineHeight: 1.18, letterSpacing: "-0.8px", marginBottom: 18,
          }}>
            Governance, Risk &<br />Compliance — unified.
          </h1>

          <p style={{ color: "rgba(255,255,255,0.6)", fontSize: 15, lineHeight: 1.75, marginBottom: 44, maxWidth: 380 }}>
            Automate evidence collection, map controls across 20+ frameworks,
            and stay audit-ready year-round — all in one platform.
          </p>

          {/* Stats */}
          <div style={{ display: "flex", gap: 0, marginBottom: 44 }}>
            {[["20+", "Frameworks"], ["800+", "Controls"], ["100%", "Uptime"]].map(({ 0: val, 1: lbl }, i) => (
              <div key={lbl} style={{
                flex: 1, paddingRight: 24,
                borderRight: i < 2 ? "1px solid rgba(255,255,255,0.12)" : "none",
                paddingLeft: i > 0 ? 24 : 0,
              }}>
                <div style={{ color: "#fff", fontSize: 30, fontWeight: 800, letterSpacing: "-0.5px" }}>{val}</div>
                <div style={{ color: "rgba(255,255,255,0.45)", fontSize: 12, fontWeight: 500, marginTop: 3 }}>{lbl}</div>
              </div>
            ))}
          </div>

          {/* Framework tags */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {["SOC 2", "ISO 27001", "GDPR", "HIPAA", "PCI DSS", "NIST CSF", "RBI"].map((f) => (
              <span key={f} style={{
                color: "rgba(255,255,255,0.65)",
                background: "rgba(255,255,255,0.07)",
                border: "1px solid rgba(255,255,255,0.12)",
                borderRadius: 6, padding: "4px 11px",
                fontSize: 12, fontWeight: 500,
              }}>{f}</span>
            ))}
          </div>
        </motion.div>

        {/* Quote */}
        <div style={{
          position: "relative", zIndex: 1,
          background: "rgba(255,255,255,0.07)",
          border: "1px solid rgba(255,255,255,0.1)",
          borderRadius: 14, padding: "20px 22px",
        }}>
          <p style={{ color: "rgba(255,255,255,0.7)", fontSize: 13, lineHeight: 1.7, marginBottom: 14 }}>
            "Tanikkai reduced our audit preparation time by 70% and gave our board
            real-time visibility into our compliance posture."
          </p>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{
              width: 32, height: 32, borderRadius: "50%",
              background: "rgba(255,255,255,0.15)",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <span style={{ color: "#fff", fontWeight: 700, fontSize: 13 }}>R</span>
            </div>
            <div>
              <div style={{ color: "#fff", fontSize: 13, fontWeight: 600 }}>Ravi Shankar</div>
              <div style={{ color: "rgba(255,255,255,0.4)", fontSize: 11 }}>CISO, Finova Technologies</div>
            </div>
          </div>
        </div>
      </div>

      {/* ── RIGHT PANEL ────────────────────────────────────── */}
      <div style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        background: "#f8f9fc",
        padding: "48px 24px",
        overflowY: "auto",
      }}>
        {/* Mobile brand */}
        <div className="flex lg:hidden" style={{ alignItems: "center", gap: 10, marginBottom: 36 }}>
          <div style={{ width: 38, height: 38, borderRadius: 10, backgroundColor: THEME, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <span style={{ color: "#fff", fontWeight: 800, fontSize: 18 }}>T</span>
          </div>
          <span style={{ color: THEME, fontWeight: 700, fontSize: 20 }}>Tanikkai GRC</span>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, delay: 0.1 }}
          style={{ width: "100%", maxWidth: 420 }}
        >
          {/* Form card */}
          <div style={{
            background: "#ffffff",
            borderRadius: 20,
            border: "1px solid #e5e7eb",
            padding: "44px 44px 36px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.04), 0 8px 32px rgba(38,37,107,0.08)",
          }}>
            {/* Header */}
            <div style={{ marginBottom: 32 }}>
              <div style={{
                width: 48, height: 48, borderRadius: 12,
                backgroundColor: THEME,
                display: "flex", alignItems: "center", justifyContent: "center",
                marginBottom: 22,
              }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2" />
                  <path d="M7 11V7a5 5 0 0110 0v4" />
                </svg>
              </div>
              <h2 style={{ color: "#111827", fontSize: 24, fontWeight: 800, letterSpacing: "-0.5px", marginBottom: 6 }}>
                Sign in
              </h2>
              <p style={{ color: "#6b7280", fontSize: 14, lineHeight: 1.5 }}>
                Access your Tanikkai GRC workspace
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: 18 }}>
                <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 7 }}>
                  Work email
                </label>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => { setUsername(e.target.value); setLoginError(''); }}
                  placeholder="you@company.com"
                  required
                  onFocus={() => setEmailFocused(true)}
                  onBlur={() => setEmailFocused(false)}
                  style={{
                    width: "100%", boxSizing: "border-box",
                    padding: "11px 14px", fontSize: 14,
                    border: `1.5px solid ${emailFocused ? THEME : "#d1d5db"}`,
                    borderRadius: 10, outline: "none",
                    color: "#111827", background: "#fff",
                    transition: "border-color 0.15s",
                    fontFamily: "inherit",
                  }}
                />
              </div>

              <div style={{ marginBottom: 24 }}>
                <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 7 }}>
                  <label style={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>
                    Password
                  </label>
                  <button
                    type="button"
                    onClick={() => setForgotPasswordStep("request")}
                    style={{
                      background: "none", border: "none", padding: 0, cursor: "pointer",
                      fontSize: 13, fontWeight: 600, color: THEME_LIGHT, fontFamily: "inherit",
                    }}
                  >
                    Forgot password?
                  </button>
                </div>
                <div style={{ position: "relative" }}>
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); setLoginError(''); }}
                    placeholder="Enter your password"
                    required
                    onFocus={() => setPassFocused(true)}
                    onBlur={() => setPassFocused(false)}
                    style={{
                      width: "100%", boxSizing: "border-box",
                      padding: "11px 42px 11px 14px", fontSize: 14,
                      border: `1.5px solid ${passFocused ? THEME : "#d1d5db"}`,
                      borderRadius: 10, outline: "none",
                      color: "#111827", background: "#fff",
                      transition: "border-color 0.15s",
                      fontFamily: "inherit",
                    }}
                  />
                  {/* <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    style={{
                      position: "absolute", right: 13, top: "50%",
                      transform: "translateY(-50%)",
                      background: "none", border: "none",
                      cursor: "pointer", color: "#9ca3af",
                      display: "flex", padding: 0,
                    }}
                  >
                    {showPassword
                      ? <EyeSlashIcon style={{ width: 18, height: 18 }} />
                      : <EyeIcon style={{ width: 18, height: 18 }} />}
                  </button> */}
                </div>
              </div>

              {loginError && (
                <div style={{
                  marginBottom: 16, padding: "10px 14px",
                  backgroundColor: "#fef2f2", border: "1.5px solid #fca5a5",
                  borderRadius: 8, display: "flex", alignItems: "flex-start", gap: 8,
                }}>
                  <svg width="16" height="16" viewBox="0 0 20 20" fill="#ef4444" style={{ marginTop: 1, flexShrink: 0 }}>
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <span style={{ fontSize: 13, color: "#dc2626", lineHeight: 1.45 }}>{loginError}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                style={{
                  width: "100%", padding: "12px 16px",
                  backgroundColor: loading ? "#9b9acc" : THEME,
                  color: "#fff", fontSize: 14, fontWeight: 700,
                  border: "none", borderRadius: 10,
                  cursor: loading ? "not-allowed" : "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                  transition: "background-color 0.18s, box-shadow 0.18s",
                  boxShadow: loading ? "none" : `0 4px 14px ${THEME}55`,
                  fontFamily: "inherit",
                }}
                onMouseEnter={(e) => { if (!loading) e.currentTarget.style.backgroundColor = THEME_LIGHT; }}
                onMouseLeave={(e) => { if (!loading) e.currentTarget.style.backgroundColor = THEME; }}
              >
                {loading ? (
                  <>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <style>{`@keyframes rsp{to{transform:rotate(360deg)}}`}</style>
                      <circle cx="12" cy="12" r="10" stroke="white" strokeWidth="3" strokeOpacity="0.25" />
                      <path d="M12 2a10 10 0 0110 10" stroke="white" strokeWidth="3" strokeLinecap="round"
                        style={{ animation: "rsp 0.75s linear infinite", transformOrigin: "12px 12px" }} />
                    </svg>
                    Signing in…
                  </>
                ) : "Sign in"}
              </button>
            </form>

            {/* Demo credentials */}
            <div style={{ marginTop: 28, paddingTop: 24, borderTop: "1px solid #f3f4f6" }}>
              <p style={{
                fontSize: 11, fontWeight: 700, color: "#9ca3af",
                textTransform: "uppercase", letterSpacing: "0.09em", marginBottom: 10,
              }}>
                Demo Access
              </p>
              <div style={{ display: "flex", flexDirection: "column" }}>
                {demoCredentials.map((cred, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => { setUsername(cred.username); setPassword(cred.password); }}
                    style={{
                      display: "flex", alignItems: "center", justifyContent: "space-between",
                      padding: "8px 10px", borderRadius: 8,
                      border: "none", background: "transparent",
                      cursor: "pointer", textAlign: "left",
                      transition: "background 0.12s",
                      fontFamily: "inherit",
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = "#f5f4ff")}
                    onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                  >
                    <span style={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>{cred.label}</span>
                    <span style={{ fontSize: 12, color: THEME, fontFamily: "'JetBrains Mono', monospace", fontWeight: 500 }}>
                      {cred.username.split("@")[0]}
                    </span>
                  </button>
                ))}
              </div>
              <p style={{ fontSize: 11, color: "#d1d5db", textAlign: "center", marginTop: 6 }}>
                Click any row to auto-fill
              </p>
            </div>
          </div>

          {/* Footer */}
          <p style={{
            textAlign: "center", fontSize: 12, color: "#9ca3af",
            marginTop: 20, display: "flex", alignItems: "center",
            justifyContent: "center", gap: 5,
          }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0110 0v4" />
            </svg>
            Secured with AWS Cognito · 256-bit TLS
          </p>
        </motion.div>
      </div>

      <NewPasswordModal
        isOpen={showNewPasswordModal}
        onClose={() => setShowNewPasswordModal(false)}
        onSubmit={handleNewPasswordSubmit}
        loading={loading}
      />
      <ForgotPasswordModal
        isOpen={forgotPasswordStep !== null}
        startAtCode={forgotPasswordStep === "confirm"}
        initialEmail={username}
        onClose={() => setForgotPasswordStep(null)}
        onDone={handlePasswordResetDone}
      />
    </div>
  );
};

export default Login;
